import { AiOutlineLeftCircle, AiOutlineRightCircle } from "react-icons/ai";
import { TextSection } from "components/TextSection";
import { HeroImageContainer } from "components/HeroImageContainer";
import heroBgMobile from "assets/content/hero-bg-mobile.jpg";
import alinaLay from "assets/content/alina-lay.png";
import { ReactComponent as BodySVG } from "assets/content/results/body.svg";
import { ReactComponent as HeartSVG } from "assets/content/results/heart.svg";
import { ReactComponent as LeafsSVG } from "assets/content/results/leafs.svg";

import { ReactComponent as ForkAndSVG } from "assets/content/results/fork_spoon.svg";
import { ReactComponent as PregnantSVG } from "assets/content/results/pregnant.svg";
import { ReactComponent as BackSideSVG } from "assets/content/results/back_side.svg";
import { ReactComponent as LungsSVG } from "assets/content/results/lungs.svg";
import { ReactComponent as StaminaSVG } from "assets/content/results/stamina.svg";
import { ReactComponent as ConsultationSVG } from "assets/svg/consultation.svg";
import { Header } from "components/Header";
import { useRef, useState, useEffect } from "react";
import { Products } from "components/Products";
import { Footer } from "components/Footer";

// Hook for scroll animations
const useScrollAnimation = (delay = 0) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [delay]);

  return { elementRef, isVisible };
};

const liAgreeStyle =
  "before:content-['✅'] before:-left-6 before:text-xl before:absolute relative";

// Animated wrapper component
const AnimatedSection: React.FC<{
  children: React.ReactNode;
  delay?: number;
  animationType?: 'fadeUp' | 'fadeIn' | 'slideLeft' | 'slideRight' | 'scale';
  className?: string;
}> = ({ children, delay = 0, animationType = 'fadeUp', className = '' }) => {
  const { elementRef, isVisible } = useScrollAnimation(delay);

  const getAnimationStyle = () => {
    const baseStyle = {
      transition: 'all 0.8s cubic-bezier(0.25, 0.8, 0.25, 1)',
    };

    switch (animationType) {
      case 'fadeUp':
        return {
          ...baseStyle,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0px)' : 'translateY(50px)',
        };
      case 'fadeIn':
        return {
          ...baseStyle,
          opacity: isVisible ? 1 : 0,
        };
      case 'slideLeft':
        return {
          ...baseStyle,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateX(0px)' : 'translateX(-50px)',
        };
      case 'slideRight':
        return {
          ...baseStyle,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateX(0px)' : 'translateX(50px)',
        };
      case 'scale':
        return {
          ...baseStyle,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'scale(1)' : 'scale(0.9)',
        };
      default:
        return baseStyle;
    }
  };

  return (
    <div ref={elementRef} className={className} style={getAnimationStyle()}>
      {children}
    </div>
  );
};

const intro = (
  <div>
    <h1>
      Шпак Метод Оксисайз NEW
    </h1>
    <p>
      <strong>
        Стимулирует сжигание жиров, дает тонус мышц, уменьшает аппетит, укрепляет женское и мужское здоровье.
      </strong>
    </p>

    <p>
      Шпак Метод Оксисайз NEW — это динамичная дыхательная тренировка, в которой сочетаются активные упражнения в статодинамике и мощные дыхательные техники. Она запускает жиросжигание и одновременно прорабатывает мышечный рельеф.
    </p>

    <p>
      <strong>Результат</strong> — не только снижение жировых объёмов, но и формирование рельефа, общего тонуса, силы, а также укрепление мышц тазового дна — доступное решение как «женских», так и «мужских» проблем.
    </p>

    <div className="bg-gradient-to-r from-rose-100 to-pink-100 p-6 rounded-lg border-l-4 border-rose-500 my-8">
      <p className="text-lg font-medium text-gray-800">
        📌 Занимает всего 10–20 минут в день и проходит один раз в год.
      </p>
    </div>

    <h2>Почему стоит попробовать?</h2>
    <ul className="list">
      <li className={liAgreeStyle}>
        <strong>Улучшает кровообращение</strong>, нормализует работу пищеварительной и репродуктивной систем, повышает гибкость и устойчивость к стрессу.
      </li>
      <li className={liAgreeStyle}>
        Благодаря <strong>насыщению тканей кислородом</strong> усиливается метаболизм, повышается энергетическая отдача, <strong>активнее сжигается жир</strong> и предотвращается его накопление.
      </li>
      <li className={liAgreeStyle}>
        Методика <strong>выводит токсины</strong>, нормализует уровень сахара в крови, <strong>снижает аппетит</strong> и устраняет психологические причины переедания. Методика подходит даже тем, кто недавно восстановился после родов или имеет гормональные сбои.
      </li>
    </ul>

    <h2>Оксисайз по Шпак Методу подходит тем, кто:</h2>
    <ul className="list">
      <li className={liAgreeStyle}>
        уже освоил <strong>базовые дыхательные практики</strong>
      </li>
      <li className={liAgreeStyle}>
        хочет перейти к <strong>телу с рельефом</strong>, а не просто «скинуть вес»
      </li>
      <li className={liAgreeStyle}>
        любит <strong>ощущать результат после каждой тренировки</strong>
      </li>
    </ul>

    <p>
      Эта тренировка особенно нравится тем, кто знаком с <strong>классическим фитнесом</strong>, но устал от бесконечных повторов и неэффективных программ.
    </p>

    <h2>Почему важно участвовать регулярно</h2>
    <p>
      Каждая тренировка в <strong>Шпак Методе уникальна</strong> и является частью годовой программы. Мы используем разные упражнение в сочетании с дыхательными техниками, и каждый раз — это <strong>новая точка входа в тело</strong>.
    </p>
    <p>
      Метод строится как <strong>система</strong>, а не как разрозненные упражнения. Поэтому важно быть в потоке — тело быстро откликается, когда получает <strong>последовательную и разнообразную нагрузку</strong>.
    </p>

    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-6">
      <p className="text-sm text-yellow-800">
        ⚠️ Имеются противопоказания. Перед началом занятий рекомендуется консультация специалиста.
      </p>
    </div>
  </div>
);

const howPass = (
  <div>
    <h2>Структура тренировочной недели</h2>
    <p>
      В течение 7 дней, каждое утро в 08:00 по московскому времени, участницы получают новый видеоурок в закрытом Telegram-канале. Он включает:
    </p>
    <ol>
      <li className={liAgreeStyle}>Детальный разбор новых упражнений</li>
      <li className={liAgreeStyle}>Тренировку, актуальную на день занятия</li>
      <li className={liAgreeStyle}>Теоретический блок</li>
    </ol>
    <p>
      В первый день подробно разбирается необходимая техника дыхания.
    </p>
    <p>
      Также в программе — лекции приглашённых специалистов по теме тренировок: гинекологов, косметологов, нейропсихолога и других экспертов, делящихся практическими советами.
    </p>

    <p>
      В итоге вы получаете отработанный комплекс упражнений на каждый день, рассчитанный на месяц.
    </p>
    <p>
      Уроки доступны в записи — вы проходите их в удобное для себя время. Доступ ко всей неделе тренировок и сопутствующим материалам сохраняется на 14 дней с момента старта.
    </p>
    <p>
      При выборе тарифа с обратной связью проверка домашних заданий проводится только в течение первых 7 дней с момента начала курса — в закрытой группе WhatsApp.
    </p>

    <h3>Что нужно для занятий</h3>
    <ul>
      <li className={liAgreeStyle}>
        Коврик или большое полотенце
      </li>
      <li className={liAgreeStyle}>По желанию: антицеллюлитный крем, крем для груди, маски, патчи — как часть ухода за собой</li>
      <li className={liAgreeStyle}>
        Пространство дома или на улице, где вам комфортно
      </li>
      <li className={liAgreeStyle}>
        Зеркало или фронтальная камера — для самостоятельного контроля выполнения упражнения
      </li>
      <li className={liAgreeStyle}>
        Одежда — любая, в которой вы чувствуете себя комфортно, можно заниматься в нижнем белье или голышом.
      </li>
      <li className={liAgreeStyle}>
        Установленное приложение Telegram и стабильный интернет
      </li>
    </ul>
  </div>
);

const liExlamationStyle =
  "before:content-['❗'] before:-left-6 before:text-xl before:absolute relative";

const rulesContent = (
  <div>
    <h2>Условия участия</h2>
    <h3><strong>Шаг 1. Оплата</strong></h3>
    <p>
      Оформляется на сайте <a href="https://ilovebodyflex.online" className="text-rose-600">ilovebodyflex.online</a> через систему Robokassa. Пожалуйста, внимательно указывайте личные данные. С ошибками невозможно предоставить доступ и поддержку.
    </p>

    <h3><strong>Шаг 2. Подключение к тренировке</strong></h3>
    <p>
      После оплаты вам необходимо отправить запрос в <strong>закрытый Telegram-канал</strong>, где будут проходить все занятия. Добавление в канал занимает до 2-х дней.
    </p>
    <p>
      Вот ссылка на канал: <a href="https://t.me/+AvJ2ToZuPKA0ZWQ6" className="text-rose-600 underline">перейти в канал.</a>
    </p>
    <p>
      Если возникли вопросы или сложности — пишите в техподдержку <a href="https://t.me/shpakmetod" className="text-rose-600">@shpakmetod</a> в Telegram (ежедневно с 9:00 до 22:00 по московскому времени).
    </p>

    <h2 className="block text-rose-500">Обратите внимание</h2>
    <p>— После начала курса оплата не возвращается и не переносится ни по каким причинам</p>
    <p>— Копирование и распространение материалов запрещено и защищено законом</p>
    <p>— Контент и стиль подачи определяются автором метода. Комментарии и предложения не рассматриваются</p>
    <p>— Перед участием убедитесь в отсутствии противопоказаний, при необходимости проконсультируйтесь с врачом</p>
    <p>— Придерживаемся принципа персональной ответственности за свое здоровье.</p>
  </div>
);

const BeforeAfter = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStartX, setTouchStartX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const { elementRef, isVisible } = useScrollAnimation();

  const img1 = require("assets/content/before-after/IMG_6223.JPG");
  const img2 = require("assets/content/before-after/IMG_6224.JPG");
  const img3 = require("assets/content/before-after/IMG_6225.JPG");
  const img4 = require("assets/content/before-after/IMG_6226.JPG");
  const img5 = require("assets/content/before-after/IMG_6227.JPG");
  const img6 = require("assets/content/before-after/IMG_6228.JPG");
  const img7 = require("assets/content/before-after/IMG_6229.JPG");
  const img8 = require("assets/content/before-after/IMG_6264.JPG");
  const img9 = require("assets/content/before-after/IMG_6231.JPG");

  const images = [
    { src: img1, alt: "Результат участницы тренировки" },
    { src: img2, alt: "Результат участницы тренировки" },
    { src: img3, alt: "Результат участницы тренировки" },
    { src: img4, alt: "Результат участницы тренировки" },
    { src: img5, alt: "Результат участницы тренировки" },
    { src: img6, alt: "Результат участницы тренировки" },
    { src: img7, alt: "Результат участницы тренировки" },
    { src: img8, alt: "Результат участницы тренировки" },
    { src: img9, alt: "Результат участницы тренировки" },
  ];

  const handleLeftClick = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleRightClick = () => {
    if (images.length > currentSlide + 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setTouchStartX(e.touches[0].clientX);
    setIsDragging(true);
    setDragOffset(0);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    const currentX = e.touches[0].clientX;
    const deltaX = currentX - touchStartX;
    setDragOffset(deltaX);
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    setIsDragging(false);
    setDragOffset(0);

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartX;
    const threshold = 50; // Минимальное расстояние для переключения

    if (Math.abs(deltaX) < threshold) return;

    if (deltaX > 0 && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    } else if (deltaX < 0 && currentSlide < images.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const carouselButtonClass =
    "absolute flex top-1/2 -translate-y-1/2 items-center justify-center p-2 rounded-full border-2 z-10 border-transparent w-14 h-14 focus:outline-none focus:shadow-outline";
  const carouselButtonLeftClass =
    carouselButtonClass +
    " left-6 md:left-[18%] lg:left-[23%]" +
    (currentSlide === 0 ? " opacity-10" : "");
  const carouselButtonRightClass =
    carouselButtonClass +
    " right-6 md:right-[18%] lg:right-[23%]" +
    (currentSlide === images.length - 1 ? " opacity-10" : "");

  return (
    <div
      ref={elementRef}
      className="prose mx-auto py-8 pb-20 max-w-full"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0px)' : 'translateY(50px)',
        transition: 'all 0.8s cubic-bezier(0.25, 0.8, 0.25, 1)'
      }}
    >
      <h2
        className="text-center mb-8"
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
          transition: 'all 0.6s cubic-bezier(0.25, 0.8, 0.25, 1)',
          transitionDelay: isVisible ? '0.2s' : '0s'
        }}
      >
        До и после Шпак Метода
      </h2>
      <div
        className="relative overflow-hidden touch-pan-y"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          cursor: isDragging ? 'grabbing' : 'grab',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'scale(1)' : 'scale(0.95)',
          transition: 'all 0.8s cubic-bezier(0.25, 0.8, 0.25, 1)',
          transitionDelay: isVisible ? '0.4s' : '0s'
        }}
      >
        <button className={carouselButtonLeftClass} onClick={handleLeftClick}>
          <AiOutlineLeftCircle className="w-14 h-14 text-rose-500 hover:text-rose-600 transition-colors" />
        </button>
        <button className={carouselButtonRightClass} onClick={handleRightClick}>
          <AiOutlineRightCircle className="w-14 h-14 text-rose-500 hover:text-rose-600 transition-colors" />
        </button>

        <div
          className="flex"
          style={{
            transform: `translateX(${-currentSlide * 100 + (isDragging ? (dragOffset / window.innerWidth) * 100 : 0)}%)`,
            transition: isDragging ? 'none' : 'transform 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)'
          }}
        >
          {images.map((image, index) => (
            <div key={index} className="w-full flex-shrink-0 flex justify-center px-4">
              <img
                src={image.src}
                alt={image.alt}
                className="w-full max-w-2xl rounded-lg shadow-lg"
              />
            </div>
          ))}
        </div>

        {/* Индикаторы слайдов с анимацией */}
        <div
          className="flex justify-center mt-8 mb-4 space-x-2"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0px)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(0.25, 0.8, 0.25, 1)',
            transitionDelay: isVisible ? '0.6s' : '0s'
          }}
        >
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className="w-3 h-3 rounded-full transition-all duration-300"
              style={{
                backgroundColor: index === currentSlide ? '#f43f5e' : '#d1d5db',
                transform: index === currentSlide ? 'scale(1.2)' : 'scale(1)',
                boxShadow: index === currentSlide ? '0 0 10px rgba(244, 63, 94, 0.5)' : 'none',
                opacity: isVisible ? 1 : 0,
                transitionDelay: isVisible ? `${0.8 + index * 0.1}s` : '0s'
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const testimonials = [
  {
    name: "daryadoshenko",
    avatar: "review-avatar-1.png",
    text: (
      <p>
        👍легко и очень просто, главное делать правильно) ❤️мне очень нравится)
        буду ждать теперь эфир с техникой пожёстче)😁 я после зарядки как
        пропеллер ношусь ...) 😘 благодарю
      </p>
    ),
    likes: 27,
  },
  {
    name: "smartcosmetolog",
    avatar: "ava-8.jpeg",
    text: (
      <p>
        Я начала с Алиной работать в августе: я маленькая и поэтому каждый кг
        виден 🙈 к сегодняшнему дню -3,5 кг 💪 в объёмах только под грудью -8 см
        🔥🔥🔥 Теперь у меня задача ещё -4 кг с оксисайз 👌 Мне нравится подход
        Алины: всё просто, делайте, как говорю, главное каждый день))) Реально
        всё работает — первый результат виден через несколько дней 💪 Это
        ГЕНИАЛЬНАЯ СИСТЕМА 🥇"
      </p>
    ),
    likes: 159,
  },
  {
    name: "katri_na93",
    avatar: "ava-2.jpeg",
    text: (
      <p>
        Не так-то просто уложить всю благодарность в одно сообщение 😍 началось
        с того, что после родов, я сильно поправилась, хоть кг и показывали
        примерно такие же цифры как до беременности, но объемы были просто
        огромными! Пол года я наблюдала за Вашей страничкой, сначала одним
        глазом, потом ждала, когда начнётся тренировка! Я заранее настраивала
        себя на плохой результат, ведь маленький ручной ребёнок весь день со
        мной, да и сил не было совсем, пока малышка спала, я тоже спала, и ещё
        безумно спина болела, дочку не могла поднимать, если только со слезами.
        После тренировки, кстати, собиралась к остеопату насчёт спины. Вот
        началась тренировка, нашла в себе силы не спать, а смотреть эфир, делала
        все с опозданием на один день. А дальше началось нечто... на третий день
        тренировки я проснулась без боли в спине... пишу и прям плачу... я на
        протяжении нескольких месяцев не могла даже лежать без боли, а тут
        такое...
      </p>
    ),
    likes: 137,
  },
  {
    name: "alinkashpak",
    avatar: "ava-1.jpeg",
    text: (
      <p>
        100% работает, а фраза «Ваша жопа в ваших рука» спасёт мир!!! Алина, не
        перестаю вас благодарить, жир тает, а энергия только прибавляется,
        участвовала уже в 2-х тренировках и жду 3-ю!!! ❤️❤️❤️😘
      </p>
    ),
    likes: 152,
  },
  {
    name: "la_skava",
    avatar: "ava-3.jpeg",
    text: (
      <p>
        Алииииииинааа! Это невероятно!!! Я в шоке, поэтому ступор в словах моей
        благодарности!!! Но! Три недели моей первой тренировки с тобой и
        тааакиие результаты!!! Грудь без изменений! Талия минус 4 см, живот
        минус 3, бёдра минус 2!!!! А ноги! Ноги!!! Я уже забыла какой они формы,
        мои прекрасные длинные ноги!! Постоянно отечные и жирноватые((( При
        росте 175 и весе 68. Сейчас объёмы 93-74-100!!!! Я навеки с Вами! Всё
        работает!!
      </p>
    ),
    likes: 152,
  },
  {
    name: "talkree78",
    avatar: "ava-4.jpeg",
    text: (
      <p>
        Привет дышу с тобой уже Неделю и не знаю или это от головы или реально
        всё так классно, живот мой уходит, хотя я пью пиво в выхи и не могу
        забыть про него, но уже не утро без дыхания, как наркотик. Встаю и бегом
        дышать с мелкой, она за мной всё повторяет 😊 Надеюсь скоро стать худой
        и подтянутой 😊 😛
      </p>
    ),
    likes: 189,
  },
  {
    name: "tan_tan",
    avatar: "ava-5.jpeg",
    text: (
      <p>
        Алина, благодарю за тренировку ❤️ Более понятного объяснения техники и
        упражнений я не слышала нигде ещё ✊ У меня тариф без обратной связи, но
        я считаю, что всё более, чем понятно 👌👌👌 Подписана давно на тебя,
        пробовала делать и #шпакметод_зарядка и упражнения в свободном доступе
        по бодифлексу, думала всё уже знаю ✌️ такая деловая, хрен там 🤚🙈🙊 В
        тренировке я совершенно по-другому начала дышать, более осознанно все 5
        этапов, прям супер 💫 Помогают твои объяснения деталей мелких, вроде как
        итак понятно, но вот именно там подкрутил, тут голову наклонил и всё,
        совсем другое дело, совсем другой результат, и живот лучше уходит под
        рёбра и на пятом этапе хочется вдохнуть как положено, а то ж когда
        самостоятельно делала, думала, ну что тяжёлого-то, задержать на 10 сек.,
        фи, я могу и дольше, ага, сейчас до 8-9 секунд дотянуть бы 😐 В общем, я
        в восторге полнейшем 👏 Хочу теперь подышать с тобой оффлайн 💫💫💫
      </p>
    ),
    likes: 189,
  },
  {
    name: "nenris77",
    avatar: "ava-6.jpeg",
    text: (
      <p>
        И вот сейчас: качество тела изменилось, целлюлит ушёл, влезла во все
        вещи. Работы ещё немного и я навеки Ваша... 💙💙💙 пью вино и ем всё,
        что хочу, дышу по утрам и слушаю чашу... доверяю и верю и дальше
        наблюдаю... как изменится моё тело, как изменюсь я и всё вокруг...
        дыхательные техники — это не только про жопу, это про всю жизнь... я
        выдохнула благодаря Вам Спасибо 🙏
      </p>
    ),
    likes: 189,
  },
  {
    name: "tatkach66",
    avatar: "ava-7.jpeg",
    text: (
      <p>
        Тренировка!! Просто супер! Мне очень нравится! Бодифлекс тоже интересен!
        Но оксисайз просто 🔥 !!! Результаты лично у меня более ощутимее!! Всё
        тело ломит от мышечной боли. Очень люблю такое состояние. Спасибо
        огромное!! Ждём продолжения!
      </p>
    ),
    likes: 189,
  },
];
const liRestrictedStyle =
  "before:content-['❌'] before:-left-6 before:text-xl before:absolute relative";

const liConsultationStyle =
  "before:content-['⚠️'] before:-left-6 before:text-xl before:absolute relative";

const contraindication = (
  <div>
    <h2>Противопоказания</h2>

    <h3 className="text-red-600 font-bold">Абсолютные:</h3>
    <ul className="list-none mb-6">
      <li className={liRestrictedStyle}>Беременность</li>
      <li className={liRestrictedStyle}>Язва желудка (в период обострения)</li>
      <li className={liRestrictedStyle}>
        Любые заболевания сердца: сердечная недостаточность, аритмия, тахикардия, перенесённый инфаркт, ишемическая болезнь сердца и т. п.
      </li>
      <li className={liRestrictedStyle}>Эпилепсия</li>
      <li className={liRestrictedStyle}>Диафрагмальная грыжа</li>
      <li className={liRestrictedStyle}>Травмы и грыжи позвоночника</li>
    </ul>
  </div>
);

const byAgreementWithTheDoctor = (
  <div>
    <h3 className="text-yellow-600 font-bold">Только по согласованию с врачом:</h3>
    <ul className="list-none">
      <li className={liConsultationStyle}>Гипертония 2 и 3 степени</li>
      <li className={liConsultationStyle}>Удалённая почка (нефрэктомия)</li>
      <li className={liConsultationStyle}>
        Серьёзные проблемы со зрением (высокая степень близорукости, глаукома и т. п.)
      </li>
      <li className={liConsultationStyle}>
        Любые заболевания сосудов, в том числе слабость сосудов
        (о ней могут свидетельствовать частые синяки, гематомы, «лопающиеся» сосуды, носовые кровотечения и пр.)
      </li>
      <li className={liConsultationStyle}>Киста яичника</li>
      <li className={liConsultationStyle}>Мочекаменная и желчекаменная болезнь (в период обострения)</li>
    </ul>

    <div className="mt-10">
      <ConsultationSVG className="w-full" />
    </div>
  </div>
);

const footerContent = (
  <div className="flex justify-around font-serif text-gray-400">
    <div>
      <div>П МОКШИН СЕРГЕЙ НИКОЛАЕВИЧ</div>
      <div>ИНН 631102456008</div>
    </div>
    <div>
      <div>Email: ilovebodyflax@gmail.com</div> <div>Договор оферты</div>
    </div>
  </div>
);

const resultIconStyle =
  "text-white text-sm flex flex-col items-center gap-4 w-52 mb-10";
const textSpanStyle = "flex justify-center text-center items-center text-sm";

const resultsContent = (
  <div className="prose min-w-full gap-x-8 bg-rose-500 p-4 text-center">
    <h1 className="mt-20 font-serif text-white">
      Результаты от техники Оксисайз NEW:
    </h1>
    <div className="mt-10 flex flex-wrap items-center justify-around">
      <div className={resultIconStyle}>
        <BodySVG />
        <div>
          <span className={textSpanStyle}>
            Создает рельеф и тонус всему телу
          </span>
        </div>
      </div>
      <div className={resultIconStyle}>
        <HeartSVG />
        <div>
          <span className={textSpanStyle}>
            Улучшает кровообращение и сердечный ритм
          </span>
        </div>
      </div>
      <div className={resultIconStyle}>
        <LeafsSVG />
        <div>
          <span className={textSpanStyle}>Обладает мощным детокс эффектом</span>
        </div>
      </div>
      <div className={resultIconStyle}>
        <PregnantSVG />
        <div>
          <span className={textSpanStyle}>
            Быстро восстанавливает после родов
          </span>
        </div>
      </div>
    </div>
    <div className="flex flex-wrap items-center justify-around">
      <div className={resultIconStyle}>
        <BackSideSVG />
        <div>
          <span className={textSpanStyle}>
            Убирает лишние килограммы и сантиметры, дает эффект "сушки" тела
          </span>
        </div>
      </div>
      <div className={resultIconStyle}>
        <ForkAndSVG />
        <div>
          <span className={textSpanStyle}>Помогает контролировать аппетит</span>
        </div>
      </div>
      <div className={resultIconStyle}>
        <LungsSVG />
        <div>
          <span className={textSpanStyle}>
            Улучшает работу всех органов и систем организма{" "}
          </span>
        </div>
      </div>
      <div className={resultIconStyle}>
        <StaminaSVG />
        <div>
          <span className={textSpanStyle}>Развивает выносливость</span>
        </div>
      </div>
    </div>
  </div>
);

const Main = ({ scrollTo }: any) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    // Предзагружаем фоновые изображения
    const mobileImg = new Image();
    const desktopImg = new Image();
    let mobileLoaded = false;
    let desktopLoaded = false;

    const checkAllLoaded = () => {
      if (mobileLoaded && desktopLoaded) {
        setImageLoading(false);
        setTimeout(() => setIsLoaded(true), 100);
      }
    };

    mobileImg.onload = () => {
      mobileLoaded = true;
      checkAllLoaded();
    };

    desktopImg.onload = () => {
      desktopLoaded = true;
      checkAllLoaded();
    };

    // Используем импортированные изображения
    mobileImg.src = heroBgMobile;
    desktopImg.src = alinaLay;
  }, []);

  return (
    <>
      {imageLoading && (
        <div className="fixed inset-0 bg-white z-50 flex items-center justify-center overflow-hidden">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-500 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Загрузка...</p>
          </div>
        </div>
      )}
      <div className="relative bg-red-100 lg:hidden">
        <div className="clip-hero-bg-white relative h-screen min-h-[600px] bg-white sm:flex">
          <div
            className="clip-hero-bg-mobile absolute flex h-full w-full bg-heroBgMobile bg-cover bg-center bg-no-repeat"
            style={{
              opacity: isLoaded ? 1 : 0,
              transform: isLoaded ? 'scale(1)' : 'scale(1.1)',
              transition: 'all 1.2s cubic-bezier(0.25, 0.8, 0.25, 1)'
            }}
          ></div>
        </div>
        <div className="flex w-full items-center justify-center">
          <div className="font-ristretto absolute bottom-12 flex w-full flex-col content-center items-center justify-center">
            <div
              className="text-red-500 font-semibold text-5xl sm:text-7xl md:text-8xl leading-none"
              style={{
                opacity: isLoaded ? 1 : 0,
                transform: isLoaded ? 'translateY(0px)' : 'translateY(30px)',
                transition: 'all 1s cubic-bezier(0.25, 0.8, 0.25, 1)',
                transitionDelay: '0.3s'
              }}
            >
              #ШпакМетод
            </div>
            <div
              className="text-cyan-600 font-semibold text-4xl sm:text-6xl md:text-8xl leading-none"
              style={{
                opacity: isLoaded ? 1 : 0,
                transform: isLoaded ? 'translateY(0px)' : 'translateY(30px)',
                transition: 'all 1s cubic-bezier(0.25, 0.8, 0.25, 1)',
                transitionDelay: '0.5s'
              }}
            >
              Оксисайз NEW
            </div>
            <div
              className="text-gray-700 mt-4 sm:mt-6 text-center max-w-lg px-4"
              style={{
                fontSize: '1.2rem',
                fontWeight: '500',
                opacity: isLoaded ? 1 : 0,
                transform: isLoaded ? 'translateY(0px)' : 'translateY(20px)',
                transition: 'all 0.8s cubic-bezier(0.25, 0.8, 0.25, 1)',
                transitionDelay: '0.7s'
              }}
            >
              Сжигает жир, снижает аппетит, поддерживает интимное здоровье
            </div>
            <div className="">
              <div
                onClick={() => scrollTo("buy")}
                className="mt-4 sm:mt-6 rounded-md bg-red-500 p-3 px-8 sm:p-4 sm:px-16 uppercase text-white cursor-pointer hover:bg-red-600 transform hover:scale-105 transition-all duration-300 shadow-lg"
                style={{
                  fontSize: '1.2rem',
                  fontWeight: '600',
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? 'translateY(0px) scale(1)' : 'translateY(20px) scale(0.9)',
                  transition: 'all 0.8s cubic-bezier(0.25, 0.8, 0.25, 1)',
                  transitionDelay: '0.9s'
                }}
              >
                Купить
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden h-screen min-h-[600px] w-auto bg-rose-200 md:hidden lg:visible lg:flex">
        <div className="clip-header-white relative flex w-2/5 bg-white bg-cover bg-center bg-no-repeat">
          <div
            className="asbolute clip-header z-10 flex w-full bg-red-100 bg-alinaLay bg-cover bg-center bg-no-repeat"
            style={{
              opacity: isLoaded ? 1 : 0,
              transform: isLoaded ? 'scale(1) translateX(0px)' : 'scale(1.05) translateX(-20px)',
              transition: 'all 1.2s cubic-bezier(0.25, 0.8, 0.25, 1)',
              transitionDelay: '0.1s'
            }}
          ></div>
        </div>
        <div className="mx-auto flex	 w-1/2 flex-wrap content-center items-center justify-center">
          <div className="mr-4 flex flex-grow-0 flex-col font-ristretto">
            <div className="flex flex-col">
              <div
                className="text-red-500 font-semibold text-8xl leading-none"
                style={{
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? 'translateX(0px)' : 'translateX(30px)',
                  transition: 'all 1s cubic-bezier(0.25, 0.8, 0.25, 1)',
                  transitionDelay: '0.3s'
                }}
              >
                #ШпакМетод
              </div>
              <div
                className="text-cyan-600 font-semibold text-8xl leading-none"
                style={{
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? 'translateX(0px)' : 'translateX(30px)',
                  transition: 'all 1s cubic-bezier(0.25, 0.8, 0.25, 1)',
                  transitionDelay: '0.5s'
                }}
              >
                Оксисайз NEW
              </div>
            </div>
            <div
              className="text-gray-700 mt-6 max-w-lg"
              style={{
                fontSize: '1.8rem',
                fontWeight: '500',
                opacity: isLoaded ? 1 : 0,
                transform: isLoaded ? 'translateX(0px)' : 'translateX(20px)',
                transition: 'all 0.8s cubic-bezier(0.25, 0.8, 0.25, 1)',
                transitionDelay: '0.7s'
              }}
            >
              Сжигает жир, снижает аппетит, поддерживает интимное здоровье
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const Oxycise = () => {
  const headerRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const introRef = useRef<HTMLDivElement>(null);
  const howRef = useRef<HTMLDivElement>(null);
  const rulesRef = useRef<HTMLDivElement>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const contraindicationRef = useRef<HTMLDivElement>(null);
  const buyRef = useRef<HTMLDivElement>(null);

  const scrollTo = (name: string) => {
    if (name === "top") window.scrollTo(0, introRef?.current?.offsetTop || 0);
    if (name === "intro") window.scrollTo(0, introRef?.current?.offsetTop || 0);
    if (name === "how") window?.scrollTo(0, howRef?.current?.offsetTop || 0);
    if (name === "buy") window?.scrollTo(0, howRef?.current?.offsetTop || 0);
    if (name === "rules")
      window?.scrollTo(0, rulesRef?.current?.offsetTop || 0);
    if (name === "testimonials")
      window?.scrollTo(0, reviewsRef?.current?.offsetTop || 0);
    if (name === "contraindication")
      window?.scrollTo(0, contraindicationRef?.current?.offsetTop || 0);
  };

  const products = [
    {
      id: 25,
      name: "Стандарт",
      description: (
        <ul className="list-none pl-4">
          <li className="font-bold">
            — Доступ к информации и тренировкам в закрытом Telegram-канале на 14 дней
          </li>
          <li className="font-bold">
            — Без обратной связи и проверки домашних заданий
          </li>
          <li className="text-sm mt-2">
            <strong>Важно:</strong> Вам необходимо самостоятельно отправить запрос на добавление в группу и внимательно указать свои данные в анкете.
          </li>
        </ul>
      ),
      price: "10 000 ₽",
      // oldPrice: "10 000 ₽",
    },
    {
      id: 26,
      name: "Стандарт плюс",
      description: (
        <ul className="list-none pl-4">
          <li className="font-bold">
            — Доступ к информации и тренировкам в закрытом Telegram-канале на 14 дней
          </li>
          <li className="font-bold">
            — Проверка только техники дыхания в общем чате WhatsApp — в течение первых 72 часов после старта курса
          </li>
          <li className="text-sm mt-2">
            <strong>Важно:</strong> Вам необходимо самостоятельно отправить запрос на добавление в группу и внимательно указать свои данные в анкете.
          </li>
        </ul>
      ),
      price: "12 000 ₽",
      // oldPrice: "13 000 ₽",
    },
    {
      id: 27,
      name: "VIP",
      description: (
        <div>
          <p className="mb-3 text-sm pl-4">
            Если ты хочешь быть уверена, что всё делаешь правильно, не согласна на "как получится", и по-настоящему стремишься к результату — этот тариф для тебя.
          </p>
          <ul className="list-none space-y-1">
            <li className="font-bold">
              — Доступ к информации и тренировкам в закрытом Telegram-канале на 14 дней
            </li>
            <li className="font-bold">
              — Проверка техники дыхания и всех упражнений в общем VIP-чате WhatsApp
            </li>
            <li className="font-bold">
              — Жёсткий режим и тотальный контроль — чтобы не осталось ни шанса «отмазаться»
            </li>
          </ul>
          <div className="text-sm mt-3 pt-2 border-t border-gray-200 pl-4">
            <strong>Важно:</strong> Проверка домашних заданий проводится только в течение первых 7 дней с момента начала курса. Проверка осуществляется в закрытой группе WhatsApp. Запрос в группу вы отправляете самостоятельно, внимательно указывая данные в анкете.
          </div>
        </div>
      ),
      price: "15 000 ₽",
      // oldPrice: "15 000 ₽",
    },
    {
      id: 35,
      name: "VIP+",
      description: (
        <div>
          <p className="mb-3 text-sm pl-4">
            Если ты хочешь личного контроля от Алины Шпак — этот тариф для тебя.
          </p>
          <ul className="list-none space-y-1">
            <li className="font-bold">
              — Доступ к информации и тренировкам в закрытом Telegram-канале на 14 дней
            </li>
            <li className="font-bold">
              — Проверка техники дыхания и всех упражнений лично Алиной Шпак в WhatsApp
            </li>
            <li className="font-bold">
              — Жёсткий режим и тотальный контроль — чтобы не осталось ни малейшего шанса «отмазаться»
            </li>
          </ul>
          <div className="text-sm mt-3 pt-2 border-t border-gray-200 pl-4">
            <strong>Важно:</strong> Проверка домашних заданий проводится только в течение первых 7 дней с момента начала курса. Проверка осуществляется в закрытой группе WhatsApp. Запрос в группу вы отправляете самостоятельно, внимательно указывая данные в анкете.
          </div>
        </div>
      ),
      price: "100 000 ₽",
    },
  ];

  return (
    <div ref={topRef} className="overflow-x-hidden">
      <Header scrollTo={scrollTo} ref={headerRef} />
      <Main scrollTo={scrollTo} />
      <div ref={introRef} />

      <AnimatedSection animationType="fadeUp">
        <TextSection content={intro} />
      </AnimatedSection>

      <AnimatedSection animationType="scale" delay={200}>
        {resultsContent}
      </AnimatedSection>

      <div ref={howRef} />
      <AnimatedSection animationType="slideLeft">
        <HeroImageContainer imageName="how-pass.png" />
      </AnimatedSection>

      <AnimatedSection animationType="fadeUp">
        <TextSection content={howPass} />
      </AnimatedSection>

      <div ref={rulesRef} />
      <AnimatedSection animationType="slideRight">
        <HeroImageContainer imageName="rules.png" />
      </AnimatedSection>

      <AnimatedSection animationType="fadeUp">
        <TextSection content={rulesContent} />
      </AnimatedSection>

      <div ref={reviewsRef} />
      <div className="h-16 bg-separator bg-repeat-x" />
      <BeforeAfter />
      <div className="h-16 bg-separator bg-repeat-x" />

      <div ref={contraindicationRef} />
      <AnimatedSection animationType="fadeUp">
        <TextSection content={contraindication} />
      </AnimatedSection>

      <AnimatedSection animationType="fadeUp" delay={100}>
        <TextSection
          className="bg-gray-100 pt-4"
          content={byAgreementWithTheDoctor}
        />
      </AnimatedSection>

      <div ref={buyRef} />
      <AnimatedSection animationType="scale">
        <Products
          active={true}
          date={"17 июля 2025"}
          products={products}
          closed={true}
          closedTitle="Набор закрыт. Ожидайте следующий"
        />
      </AnimatedSection>

      <AnimatedSection animationType="fadeIn">
        <Footer />
      </AnimatedSection>
    </div>
  );
};
